import {connect} from 'react-redux';

import {
  AppParametricSelector,
  createSelector,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {InboxModel} from '../../../../models/api/inboxModel';
import {CollapsedItemKeyPrefixesEnum} from '../../../../models/api/localPreferencesModel';
import {
  CellsRefinementOptionsEnum,
  RefinementOptionModel,
} from '../../../../models/api/refinementOptionModel';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {VisualWorkspaceInbox} from '../../../../models/api/visualWorkspaceModel';
import {InboxesFiltersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplayChevronActionsEnum,
  WorkspaceDisplayChevronSectionsEnum,
} from '../../../../services/tracking';
import {AppState} from '../../../../store/appState';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {getUserTeamParticipatingInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {toggleExpandItemAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {
  getFindIsExpanded,
  getRefinementOptions,
} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {makeGetCountersVisibility} from '../../../../store/data/preferences/countersSelectors';
import {
  getShouldAlphabetizeWorkspace,
  getShouldUseOpenFilterForTeams,
  getWasOnboardingCompleted,
} from '../../../../store/data/preferences/preferencesSelectors';
import {makeGetWorkspacesSelectedRefinements} from '../../../../store/data/refinements/refinementsSelectors';
import {getIsUserFirstGlobalAdmin} from '../../../../store/data/teammates/teammatesSelectors';
import {getUserTeams} from '../../../../store/data/teams/teamsSelectors';
import {makeGetVisualWorkspaceVisibleInboxesForTeam} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceSelectors';
import {
  getHasActionCtasBundle,
  getHasCellsEmptySetupActions,
  getHasP2Refinements,
} from '../../../../store/featureFlags/featureFlagsSelectors';
import {
  buildInboxRefinementKey,
  buildTeamRefinementKey,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersSelectors';
import {getShouldAddTeamInbox} from '../../../../store/stateless/startGuideSelectors';
import {TeamWorkspaceInboxes} from './teamWorkspaceInboxes';

/*
 * Constants.
 */

const defaultIsTeamInboxesExpanded = true;

/*
 * Props.
 */

export interface TeamWorkspaceInboxesContainerProps {
  team: TeamModel;
  visualWorkspaceInboxesForTeam: ReadonlyArray<VisualWorkspaceInbox>;
}

/*
 * Selectors.
 */

/** Extract the specified team from the props. */
function getTeam(_state: AppState, props: TeamWorkspaceInboxesContainerProps) {
  return props.team;
}

function getVisualWorkspaceInboxesForTeam(_state: AppState, props: TeamWorkspaceInboxesContainerProps) {
  return props.visualWorkspaceInboxesForTeam;
}

/** Find the team namespace from the teams. */
function makeGetTeamNamespace() {
  return createSelector([getTeam], (team) => team.namespace);
}

/** Find the filter to use in team context. */
const getTeamContextFilter = createSelector(
  [getShouldUseOpenFilterForTeams],
  (shouldShowTeammateTabsInTeamContext) =>
    shouldShowTeammateTabsInTeamContext ? InboxesFiltersEnum.OPEN : InboxesFiltersEnum.UNASSIGNED,
);

/** Find the selected filter for the specified team. */
function makeGetSelectedTeamFilter<P>(
  getSelectedTeamRefinement: AppParametricSelector<P, RefinementOptionModel | undefined>,
) {
  return createSelector(
    [getTeamContextFilter, getSelectedTeamRefinement, getShouldUseOpenFilterForTeams],
    (teamContextFilter, selectedRefinement, shouldUseOpenFilterForTeams) =>
      !shouldUseOpenFilterForTeams && selectedRefinement?.type === CellsRefinementOptionsEnum.ASSIGNEE
        ? InboxesFiltersEnum.ASSIGNED
        : teamContextFilter,
  );
}

function makeGetSelectedTeamRefinement() {
  return createSelector(
    [getHasP2Refinements, getTeam, getRefinementOptions],
    (hasP2Refinements, team, refinementOptions) => {
      if (!hasP2Refinements) {
        return undefined;
      }

      const key = buildTeamRefinementKey(team.id);

      return refinementOptions[key];
    },
  );
}

function buildTeamInboxesExpandedKey(teamId: number) {
  return `${CollapsedItemKeyPrefixesEnum.TEAM_INBOXES}${teamId}`;
}

// Find the expanded state for the specified team. */
function makeGetIsExpanded() {
  return createSelector([getTeam, getFindIsExpanded], (team, findIsExpanded) =>
    findIsExpanded(buildTeamInboxesExpandedKey(team.id), defaultIsTeamInboxesExpanded),
  );
}

/** If there are team inboxes show the toggle (even if user has the demo team inbox). */
const getShouldHideToggleExpand = createSelector(
  [getUserTeamParticipatingInboxes, getHasCellsEmptySetupActions],
  (inboxes, hasCellsEmptySetupActions) => inboxes.length < 1 && hasCellsEmptySetupActions,
);

const getShouldShowDemoInboxTip = createSelector(
  [
    getHasActionCtasBundle,
    getShouldAddTeamInbox,
    getUserTeams,
    getIsUserFirstGlobalAdmin,
    getWasOnboardingCompleted,
  ],
  (hasActionsCtasBundle, shouldAddTeamInbox, userTeams, isUserFirstGlobalAdmin, wasOnboardingCompleted) =>
    // Show the tip if we: 1. have the proper feature flag 2. only have demo inboxes (or none) 3. have only one team.
    // 4. are the first global admin and 5. completed onboarding.
    hasActionsCtasBundle &&
    shouldAddTeamInbox &&
    userTeams.length === 1 &&
    isUserFirstGlobalAdmin &&
    wasOnboardingCompleted,
);

function makeGetHasEditInboxPermission() {
  return createSelector([getHasPermission, makeGetTeamNamespace()], (hasPermission, namespace) =>
    hasPermission(namespace, PermissionsEnum.INBOX_UPDATE),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TeamWorkspaceInboxesContainerProps>()(() => {
  const getInboxes = makeGetVisualWorkspaceVisibleInboxesForTeam(getVisualWorkspaceInboxesForTeam);
  const getSelectedTeamRefinement = makeGetSelectedTeamRefinement();
  const getInboxesSelectedRefinements = makeGetWorkspacesSelectedRefinements<
    TeamWorkspaceInboxesContainerProps,
    InboxModel
  >(getInboxes, (inbox) => buildInboxRefinementKey(inbox.id));
  return {
    selectedFilter: makeGetSelectedTeamFilter(getSelectedTeamRefinement),
    selectedRefinement: getSelectedTeamRefinement,
    shouldUseOpenFilterForTeams: getShouldUseOpenFilterForTeams,
    inboxes: getInboxes,
    inboxesSelectedRefinements: getInboxesSelectedRefinements,
    countersVisibility: makeGetCountersVisibility(makeGetTeamNamespace()),
    isExpanded: makeGetIsExpanded(),
    shouldHideToggleExpandClick: getShouldHideToggleExpand,
    shouldShowDemoInboxTip: getShouldShowDemoInboxTip,
    shouldAlphabetizeWorkspace: getShouldAlphabetizeWorkspace,
    hasEditPermission: makeGetHasEditInboxPermission(),
  };
});

export const TeamWorkspaceInboxesContainer = connect(
  mapStateToProps,
  {
    toggleExpandItem: toggleExpandItemAction,
  },
  (stateProps, dispatchProps, ownProps) => {
    const onToggleExpandClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CHEVRON_CLICK, {
        location: WorkspaceDisplayChevronSectionsEnum.TEAM_INBOXES,
        action: stateProps.isExpanded
          ? WorkspaceDisplayChevronActionsEnum.COLLAPSE
          : WorkspaceDisplayChevronActionsEnum.EXPAND,
      });
      dispatchProps.toggleExpandItem(
        buildTeamInboxesExpandedKey(ownProps.team.id),
        defaultIsTeamInboxesExpanded,
      );
    };

    // Merge the props.
    return {
      ...stateProps,
      ...ownProps,
      inboxes: stateProps.inboxes,
      onToggleExpandClick: stateProps.shouldHideToggleExpandClick ? undefined : onToggleExpandClick,
    };
  },
)(TeamWorkspaceInboxes);
